import { Injectable } from '@angular/core';
import { AppInfo, Config, HttpMethod, WorkerPostRequest, WorkerPostResponse } from '@mitel/cloudlink-sdk';

import { environment } from 'src/environments/environment';
import { BillingAsyncJobItem, BillingAsyncJobType, Tenant, TenantClientAction, TenantPostPutRequest, TenantSipTrunk, TenantType, ZappaTenantStatus } from '../interfaces/tenants';
import { TranslateService } from '@ngx-translate/core';
import { Util } from '../utils/Util';
import { asyncJobStatus } from '../constants/constants';
import { HttpResponseStatusCode, zappaPhoneCodes } from './../constants/constants';

const logContext = 'TenantsService';

// Billing API for tenants (Zappa, Unify phone)
@Injectable({
    providedIn: 'root'
})

export class TenantsService {

  private tenantsService: any;
  private readonly manageSIPTrunksActivityId = environment.manageSIPTrunksActivityId;

  constructor( private translateService: TranslateService ) {
      
    this.tenantsService = {
      rootUrl: environment.billingUrl,
      workflowUrl: environment.workflowUrl,
      _http: <HttpMethod>Config.httpService,
      _appConfig: <AppInfo> Config.appInfo,

      // Tenants
      async getTenants(): Promise<any> {
        let headers = this._appConfig?.getAppHeaders();
        let url = `${this.rootUrl}/tenants?$expand=status`;
        return await this._http.get({ url, headers }).then(response => response.body);
      },

      async getTenant(tenantId: string): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}?$expand=status`
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.get({ url, headers }).then(response => response.body);
      },
  
      async getTenantStatus(tenantId: string): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}/status`
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.get({ url, headers }).then(response => response.body);
      },

      async deleteTenant(tenantId: string, jobTag: any, extraParams?: string): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}` + (extraParams ?? '');
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.delete({ url, headers, body: { clientJobTag: jobTag } }).then(response => response.body);
      },

      async putTenant(tenantId: string, tenantData: TenantPostPutRequest): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}`;
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.put({url, headers, body: tenantData }); //.then((response: any) => response.body);
      },

      async postTenant(tenantData: TenantPostPutRequest): Promise<any> {
        let url = `${this.rootUrl}/tenants`;
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.post({ url, headers, body: tenantData }); //.then((response: any) => response.body);
      },

      // Tenants sip trunks
      async getTenantSipTrunks(tenantId: string): Promise<any> {
        let headers = this._appConfig?.getAppHeaders();
        let url = `${this.rootUrl}/tenants/${tenantId}/sipTrunks`;
        console.log("GET ALL tenant sip trunks url", url)
        return await this._http.get({ url, headers }).then(response => response.body);
      },

      async postTenantSipTrunks(tenantId: string, tenantData: TenantSipTrunk): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}/sipTrunks`;
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.post({ url, headers, body: tenantData }).then((response: any) => response.body);
      },

      async getTenantSipTrunk(tenantId: string, trunkId: string): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}/sipTrunks/${trunkId}`
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.get({ url, headers }).then(response => response.body);
      },

      async deleteTenantSipTrunk(tenantId: string, trunkId: string): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}/sipTrunks/${trunkId}`
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.delete({ url, headers }).then(response => response.body);
      },

      // Tenants reports
      /* istanbul ignore next */ 
      async getTenantReport(tenantId: string, reportId: string): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}/reports/${reportId}`
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.get({ url, headers }).then(response => response.body);
      },
      /* istanbul ignore next */ 
      async createTenantReport(tenantId: string): Promise<any> {
        let url = `${this.rootUrl}/tenants/${tenantId}/reports`
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.get({ url, headers }).then(response => response.body);
      },

      // async jobs
      async getAsyncJobs(): Promise<any> {
        let headers = this._appConfig?.getAppHeaders();
        let url = `${this.rootUrl}/async-jobs`;
        console.log("GET billing async jobs url", url)
        return await this._http.get({ url, headers }).then(response => response.body);
      },

      async getAsyncJob(jobId: string): Promise<any> {
        let url = `${this.rootUrl}/async-jobs/${jobId}`
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.get({ url, headers }).then(response => response.body);
      },

      async putAsyncJob(jobId: string, jobData: any): Promise<any> {
        let url = `${this.rootUrl}/async-jobs/${jobId}`;
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.put({url, headers, body: jobData }).then((response: any) => response.body);
      },

      async deleteAsyncJobs(jobId: string): Promise<any> {
        let url = `${this.rootUrl}/async-jobs/${jobId}`
        let headers = this._appConfig?.getAppHeaders();
        return await this._http.delete({ url, headers }).then(response => response.body);
      },

      async runWorkerByActivityId(activityId: string, body: WorkerPostRequest, sync: boolean): Promise<WorkerPostResponse> {
        const syncQuery = sync ? '?sync=true' : '';
        let url = `${this.workflowUrl}/activities/${activityId}/workers${syncQuery}`;
        return await this._http.post({ url, body: body }).then((response: any) => response.body);
      },

      async getUserLicenses(userId: string, filter: string): Promise<any[] | undefined> {
        const url = `${this.rootUrl}/users/${userId}/licenses` + (filter ? `?$filter=${filter}` : '');
        return await this._http.get({ url }).then((response: any) => response.body);
      }
    }
  }
    	
  getTenants(): Promise<Tenant[]> // is there any reason to cache this list?
	{
    return new Promise<Tenant[]>(async (resolve, reject) => {
      let tenantsList: Tenant[] = [];
      try {
        let result = await this.tenantsService.getTenants();
        if ( result && result.count > 0 && result._embedded && result._embedded.items )
        {
          tenantsList = result._embedded.items.map( tenant => {

            return {
              tenantId: tenant.tenantId,
              tenantType: tenant.tenantType,
              zoomTenant: tenant.zoomTenant,
              unifyPhoneTenant: tenant.unifyPhoneTenant,
              status: tenant.status
            }
            // It is not returning the redirect url here. Is the micro even returning it at this point? (no I checked) 
            // Is that something that will be required in the future? (unknown, but probably not)
            // Maybe in the future just return items? This mapping originally occurred because of the constant churn
            // Is it stable enough to return items or should I continue to map?
            // Last time I checked, the status was not being returned even though it was suppose to. Testing with status
          });
        }
        resolve(tenantsList);  
      }
      catch (err)
      {
        console.error(`${logContext}.getTenants failed`, err);
        // wondering if I should not convert like the rest of the methods.
        // this method is not used much at this point
        let errMsg = this.errorMessageConversion( err );
        reject(errMsg);
      }
    });
	}

  async getTenantById(tenantId: string): Promise<Tenant>
	{
		return this.tenantsService.getTenant(tenantId).then(tenant => {
      if ( tenant )
      {
        let mappedTenant: Tenant = {
          tenantId: tenant.tenantId,
          tenantType: tenant.tenantType,
          zoomTenant: tenant._embedded.zoomTenant,
          unifyPhoneTenant: tenant._embedded.unifyPhoneTenant,
          status: tenant._embedded.status,
          redirectUrl: tenant.redirectUrl
        }
        return mappedTenant;
      }
      return undefined;
    });
    //should I try to decipher the error here?
	}

	getTenantByType(type: TenantType): Promise<Tenant>
	{
    return this.getTenants().then(tenants => {
      if ( tenants && tenants.length > 0 )
      {
        let tenant = tenants.find( item => item.tenantType == type );
        return tenant;
      }
      return undefined;
    });
    //should I try to decipher the error here?
	}

	async createTenant( tenantConfig: Tenant ): Promise<Tenant>
	{
		let tenant: TenantPostPutRequest = {
      tenantType: tenantConfig.tenantType,
      zoomTenant: tenantConfig.zoomTenant,
      unifyPhoneTenant: tenantConfig.unifyPhoneTenant,
      clientRedirectUrl: tenantConfig.clientRedirectUrl
    }
    return this.tenantsService.postTenant(tenant).then(response => {
      // return it in the same format is used in get
      let newTenant = response?.body;
      console.log("Returned post response", response)

      if ( newTenant )
      {
        let mappedTenant: Tenant = {
          tenantId: newTenant.tenantId,
          tenantType: newTenant.tenantType,
          zoomTenant: newTenant._embedded.zoomTenant,
          unifyPhoneTenant: newTenant._embedded.unifyPhoneTenant,
          status: newTenant._embedded.status,
          redirectUrl: newTenant.redirectUrl,
          action: response.statusCode == 202 ? TenantClientAction.MUST_AUTH : undefined
        }
        return mappedTenant;
      }
    })
	}

	async updateTenant( tenantConfig: Tenant): Promise<Tenant>
	{
		let tenant: TenantPostPutRequest = {
        tenantType: tenantConfig.tenantType,
        zoomTenant: tenantConfig.zoomTenant,
        unifyPhoneTenant: tenantConfig.unifyPhoneTenant,
        clientRedirectUrl: tenantConfig.clientRedirectUrl
    }

    return this.tenantsService.putTenant(tenantConfig.tenantId, tenant).then(response=> {
      let newTenant = response?.body;
      console.log("Returned put response", response)
      // return it in the same format is used in get
      if ( newTenant )
        {
          let mappedTenant: Tenant = {
            tenantId: newTenant.tenantId,
            tenantType: newTenant.tenantType,
            zoomTenant: newTenant._embedded.zoomTenant,
            unifyPhoneTenant: newTenant._embedded.unifyPhoneTenant,
            status: newTenant._embedded.status,
            redirectUrl: newTenant.redirectUrl,
            action: response.statusCode == 202 ? TenantClientAction.MUST_AUTH : undefined
          }
          return mappedTenant;
        }
    })
	}

	async deleteTenant( tenantId: string, jobTag: any, extraParams?: string ): Promise<any> // return depends on the extraParams
	{
		return this.tenantsService.deleteTenant(tenantId, jobTag, extraParams);
	}

  async getTenantStatus( tenantId: string ): Promise<any>
  {
    return this.tenantsService.getTenantStatus( tenantId );
    //should I try to decipher the error here?
  }

  async getTenantSipTrunks(tenantId: string): Promise<TenantSipTrunk[]> {
    //using global workflow
    try {
      let sipTrunkList: any[] = [];
      let body = {
        inputs: {
          method: 'GET',
          parameters: { 'tenantId': tenantId },
          body: {},
          endpoint: `/tenants/${tenantId}/sipTrunks`
        }
      };

      // runWorkersByActivityId returns workerId, outputs: {statusCode, headers, responsebody }, links
      let result = await this.tenantsService.runWorkerByActivityId(this.manageSIPTrunksActivityId, body, true);

      if (result?.outputs?.statusCode == HttpResponseStatusCode.SUCCESSFUL_OK) {
        const responseBody = JSON.parse(result?.outputs?.responsebody);
        if (responseBody?.count > 0 && responseBody?._embedded?.items) {
          sipTrunkList = responseBody._embedded.items.map(sipTrunk => {
            return {
              trunkId: sipTrunk.trunkId,
              trunkName: sipTrunk.trunkName,
              username: sipTrunk.username,
              password: sipTrunk.password,
              realm: sipTrunk.realm,
              type: sipTrunk.type,
              ip: sipTrunk.ip,
              port: sipTrunk.port,
              protocol: sipTrunk.protocol
            }
          });
        }
        return sipTrunkList;
      } else  {
        const error = result?.outputs?.responsebody;
        console.error(`${logContext}.getTenantSipTrunks failed `, error);
        throw error;
      }
    }
    catch (error) {
      console.error(`${logContext}.getTenantSipTrunks failed `, error);
      throw error;
    }
	}

  async createTenantSipTrunk( tenantId: string, tenantSipTrunkData: TenantSipTrunk ): Promise<TenantSipTrunk> {
    //using global workflow
    try {
      let sipTrunk: TenantSipTrunk;
      let body = {
        inputs: {
          method: 'POST',
          parameters: { 'tenantId': tenantId },
          body: {
            'trunkName': tenantSipTrunkData.trunkName,
            'type': tenantSipTrunkData.type,
            'ip': tenantSipTrunkData.ip,
            'port': tenantSipTrunkData.port,
            'protocol': tenantSipTrunkData.protocol
          },
          endpoint: `/tenants/${tenantId}/sipTrunks`
        }
      };
      // runWorkersByActivityId returns workerId, outputs: {statusCode, headers, responsebody }, links
      let result = await this.tenantsService.runWorkerByActivityId(this.manageSIPTrunksActivityId, body, true);

      if (result?.outputs?.statusCode == HttpResponseStatusCode.SUCCESSFUL_CREATED) {
        sipTrunk = JSON.parse(result?.outputs?.responsebody);
        return sipTrunk;
      } else {
        const error = result?.outputs?.responsebody;
        console.error(`${logContext}.createTenantSipTrunk failed `, error);
        throw error;
      }
    }
    catch (error) {
      console.error(`${logContext}.createTenantSipTrunk failed `, error);
      throw error;
    }
	}  
  
  async updateTenantSipTrunk( tenantId: string, tenantSipTrunkData: TenantSipTrunk ): Promise<TenantSipTrunk> {
    //using global workflow
    try {
      let sipTrunk: TenantSipTrunk;
      let body = {
        inputs: {
          method: 'PATCH',
          parameters: { 'tenantId': tenantId },
          body: {
            'trunkName': tenantSipTrunkData.trunkName,
            'ip': tenantSipTrunkData.ip,
            'port': tenantSipTrunkData.port
          },
          endpoint: `/tenants/${tenantId}/sipTrunks/${tenantSipTrunkData.trunkId}`
        }
      };
      // runWorkersByActivityId returns workerId, outputs: {statusCode, headers, responsebody }, links
      const result = await this.tenantsService.runWorkerByActivityId(this.manageSIPTrunksActivityId, body, true);

      if (result?.outputs?.statusCode == HttpResponseStatusCode.SUCCESSFUL_OK) {
        sipTrunk = JSON.parse(result?.outputs?.responsebody);
        return sipTrunk;
      } else {
        const error = result?.outputs?.responsebody;
        console.error(`${logContext}.updateTenantSipTrunk failed `, error);
        throw error;
      }
    }
    catch (error) {
      console.error(`${logContext}.updateTenantSipTrunk failed `, error);
      throw error;
    }
	}

  getTenantSipTrunkById(tenantId: string, trunkId: string): Promise<TenantSipTrunk>
	{
		return this.tenantsService.getTenantSipTrunkById(tenantId).then(sipTrunk => {
      if ( sipTrunk )
      {
        let mappedSipTrunk: TenantSipTrunk = {
          trunkId: sipTrunk.tenantId,
          trunkName: sipTrunk.trunkName,
          username: sipTrunk.username,
          password: sipTrunk.password,
          realm: sipTrunk.realm,
          type: sipTrunk.type,
          ip: sipTrunk.ip,
          port: sipTrunk.port,
          protocol: sipTrunk.protocol,        
        }
        return mappedSipTrunk;
      }
      return undefined;
    });
    //should I try to decipher the error here?
	}

	async deleteTenantSipTrunk( tenantId: string, trunkId: string ): Promise<any> {
    //using global workflow
		try {
      let body = {
        inputs: {
          method: 'DELETE',
          parameters: { 'tenantId': tenantId, 'trunkId': trunkId },
          body: {},
          endpoint: `/tenants/${tenantId}/sipTrunks/${trunkId}`
        }
      };
      
      // runWorkersByActivityId returns workerId, outputs: {statusCode, headers, responsebody }, links
      let result = await this.tenantsService.runWorkerByActivityId(this.manageSIPTrunksActivityId, body, true);

      if (result?.outputs?.statusCode == HttpResponseStatusCode.SUCCESSFUL_NO_CONTENT) {
        return true;
      } else {
        const error = result?.outputs?.responsebody;
        console.error(`${logContext}.deleteTenantSipTrunk failed `, error);
        throw error;
      }
    }
    catch (error) {
      console.error(`${logContext}.deleteTenantSipTrunk failed `, error);
      throw error;
    }
	}

  // reports generation - not in use at this point
  /* istanbul ignore next */ 
  createTenantReport( tenantId: string ): Promise<string>
	{
    return this.tenantsService.createTenantReport(tenantId );
	}

  // reports generation - not in use at this point
  /* istanbul ignore next */ 
  getTenantReport( tenantId: string, reportId: string ): Promise<any>
  {
    return this.tenantsService.getTenantReport(tenantId, reportId );
  }

  // async jobs, at this point the swagger shows no odata
  getAsyncJobs(): Promise<BillingAsyncJobItem[]>
  {
    return new Promise<any[]>(async (resolve, reject) => {
      let asyncJobsList: any[] = [];
      try {
        let result = await this.tenantsService.getAsyncJobs();
        if ( result && result.count > 0 && result._embedded && result._embedded.items )
        {
          console.log("Received these async jobs", result._embedded.items)

          asyncJobsList = result._embedded.items;
        }

        console.log("RETURNED these async jobs", asyncJobsList)
        resolve(asyncJobsList);  
      }
      catch (err)
      {
        // should I be converting the error here? Most of these methods just throw it back for the client to handle it
        console.error(`${logContext}.getAsyncJobs failed`, err);
        let errMsg = this.errorMessageConversion( err );
        reject(errMsg);
      }
    });
  }

  async getAsyncJobById(jobId: string): Promise<BillingAsyncJobItem>
	{
		return this.tenantsService.getAsyncJob(jobId).then(asyncJob => {
      if ( asyncJob )
      {
        let mappedAsyncJob: BillingAsyncJobItem = {
          jobId: asyncJob.jobId,
          status: asyncJob.status,
          jobType: asyncJob.jobType,
          result: asyncJob.result
        }
        return mappedAsyncJob;
      }
      else
        return undefined;
    });
    //should I try to decipher the error here?
	}

  // TBD - the swagger does not seem correct at this point and
  // I don't know how this might be used
  /* istanbul ignore next */ 
  async updateAsyncJob( jobId: string, jobData: any): Promise<any>
	{
    return this.tenantsService.putAsyncJob(jobId, jobData);
	}
  /* istanbul ignore next */ 
	async deleteAsyncJob( jobId: string ): Promise<any> // no idea what this return could be
	{
		return this.tenantsService.deleteAsyncJob(jobId);
	}

	async getUserLicenses(userId: string, filter?: string): Promise<any[]> {
		try {
			return await this.tenantsService.getUserLicenses(userId, filter);
		} catch (err) {
			console.error(`${logContext}.getUserLicenses failed`, err);
			let errMsg = this.errorMessageConversion(err);
			throw errMsg;
		}
	}

	async doesUserHaveZoomLicense(userId: string): Promise<boolean> {
		const licenseFilter = "licenseName eq 'zoomPSI' and status eq 'pre-licensed'";
		try {
			const licenses = await this.getUserLicenses(userId, licenseFilter);
			if (licenses?.length > 0) {
				const zoomLicense = licenses.find(license => { return license.name == zappaPhoneCodes.USER_PRODUCT; });
				if (zoomLicense?.enable) {
					return true;
				}
			}
		} catch (err) {
			console.error(`${logContext}.doesUserHaveZoomLicense failed`, err);
		}
		return false;
	}

  // don't even know if this is valid
  errorMessageConversion( error: any ): string // no idea if this applies here
  {
    if (!error)
      return undefined;

    let prefixMsg = '';

    // this statusCode check was specific to the jira issue but we might want to
    // expand this based on feedback for the other statusCodes
    if ( error?.statusCode === 504 )
      prefixMsg = this.translateService.instant( 'app-errors.504-general-message' ) + ' ';

    return prefixMsg + (error?.body?.error?.msg 
      ?? error.message
      ?? error.msg
      ?? error.body?.msg
      ?? error.body?.message
      ?? error.statusText
      ?? error.statusCode?.toString()
      ?? error);
  } 
}

